// src/components/admin/modals/SendReminderModal.jsx
import React, { useState, useMemo } from 'react';
import { Bell, X, AlertTriangle } from 'lucide-react';
import { createPortal } from 'react-dom';
import axios from '../../../utils/axios';
import { showToast } from '../../../utils/toast';
import { formatDate } from '../../../utils/utils.global';

// ---------------------------------------------------------------------------
// Reminders are only useful for invitations that have NOT yet received a
// final response. Accepted and expired are included because the editor may
// want to nudge reviewers who accepted but are late, or reach out after
// expiry. Declined / canceled / completed are excluded — a response has
// already been received and no reminder will change that.
//
// `pending` is kept as an alias for `invite_sent` for legacy rows.
// ---------------------------------------------------------------------------
export const REMINDER_ELIGIBLE_STATUSES = ['invite_sent', 'pending', 'accepted', 'expired'];

export const isReminderEligible = (invitation) => {
  if (!invitation) return false;
  const status = invitation.status || invitation.invitation_status;
  return REMINDER_ELIGIBLE_STATUSES.includes(status);
};

// Parse MySQL YYYY-MM-DD as local so day arithmetic doesn't shift.
const parseInvitationDate = (value) => {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const parsed = new Date(value);
  return isNaN(parsed) ? null : parsed;
};

// Extract the numeric DB id from a formatted invitation id like "INV-2026-001".
const numericInvitationId = (invitation) => {
  if (!invitation) return null;
  const id = invitation.id;
  if (typeof id === 'number') return id;
  if (typeof id === 'string') {
    const parts = id.split('-');
    return parts[parts.length - 1];
  }
  return null;
};

const ModalShell = ({ onClose, children }) => {
  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>,
    document.body
  );
};

/**
 * SendReminderModal
 *
 * Sends a reminder email for one or more invitations. Renders inline into a
 * portal so it sits above every other modal.
 *
 * Props:
 *   invitations   — array of invitation objects (each with `id`, `title`,
 *                   `manuscriptId`, `invitedTo`, `invitedToEmail`, `status`,
 *                   `expiryDate`, `reminderCount`)
 *   onClose       — callback to close the modal
 *   onSuccess     — callback after all reminders are sent (refetch list)
 *   apiBase       — endpoint prefix, default '/api/semperfi/invitations'
 */
const SendReminderModal = ({
  invitations = [],
  onClose,
  onSuccess,
  apiBase = '/api/semperfi/invitations'
}) => {
  const [notes, setNotes] = useState('');
  const [extendExpiry, setExtendExpiry] = useState(false);
  const [extendDays, setExtendDays] = useState('');
  const [sending, setSending] = useState(false);

  // Only reminders-eligible invitations make it into the request; anything
  // else in the array is silently skipped and reported at the end.
  const eligible = useMemo(
    () => invitations.filter(isReminderEligible),
    [invitations]
  );

  // Preview of the new expiry for the single-invitation case. For bulk sends,
  // each invitation's new date is calculated independently server-side.
  const previewNewExpiry = useMemo(() => {
    if (!extendExpiry || eligible.length !== 1) return null;
    const inv = eligible[0];
    const base = parseInvitationDate(inv.expiryDate || inv.invitation_expiry_date) || new Date();
    const days = parseInt(extendDays, 10);
    if (!days || isNaN(days) || days <= 0) return null;
    const next = new Date(base);
    next.setDate(next.getDate() + days);
    return next;
  }, [extendExpiry, extendDays, eligible]);

  const handleConfirm = async () => {
    if (eligible.length === 0) {
      showToast('No eligible invitations to remind', 'error');
      return;
    }

    setSending(true);
    let sentCount = 0;
    let failedCount = 0;
    const skippedCount = invitations.length - eligible.length;

    // Reminders are sent individually so each email is tailored to its own
    // invitation and any per-invitation failure doesn't block the rest.
    for (const invitation of eligible) {
      try {
        const numericId = numericInvitationId(invitation);
        await axios.post(`${apiBase}/${numericId}/remind`, {
          notes,
          extendDays:
            extendExpiry && extendDays
              ? parseInt(extendDays, 10)
              : undefined,
        });
        sentCount++;
      } catch (error) {
        console.error('Error sending reminder:', error);
        failedCount++;
      }
    }

    setSending(false);
    onClose?.();

    // Report outcome. The shape of the message reflects what actually happened.
    if (failedCount === 0 && skippedCount === 0) {
      showToast(
        `Reminder sent to ${sentCount} invitation${sentCount !== 1 ? 's' : ''}`,
        'success'
      );
    } else if (failedCount === 0) {
      showToast(
        `Reminder sent to ${sentCount} invitation${sentCount !== 1 ? 's' : ''} (${skippedCount} skipped)`,
        'success'
      );
    } else {
      showToast(
        `Sent ${sentCount}, failed ${failedCount}${skippedCount ? `, skipped ${skippedCount}` : ''}`,
        sentCount === 0 ? 'error' : 'info'
      );
    }

    onSuccess?.();
  };

  return (
    <ModalShell onClose={sending ? undefined : onClose}>
      <div className="p-6 overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center">
            <div className="flex-shrink-0 w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <Bell className="w-6 h-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {eligible.length === 1 ? 'Send Manual Reminder' : 'Send Bulk Reminders'}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {eligible.length === 1
                  ? `Send a reminder email to ${eligible[0].invitedToEmail || eligible[0].invited_user || 'the recipient'}`
                  : `Send reminders to ${eligible.length} invitation${eligible.length !== 1 ? 's' : ''}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={sending}
            className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Selected invitations preview */}
        {eligible.length > 0 && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg max-h-40 overflow-y-auto">
            {eligible.length === 1 ? (
              <>
                <p className="text-sm font-medium text-gray-700">Manuscript:</p>
                <p className="text-sm text-gray-900 mt-1">{eligible[0].title}</p>
                <p className="text-xs text-gray-500 mt-1">{eligible[0].manuscriptId}</p>
                <p className="text-sm font-medium text-gray-700 mt-3">Status:</p>
                <p className="text-xs text-gray-500 mt-1">{eligible[0].status}</p>
                {eligible[0].reminderCount > 0 && (
                  <p className="text-xs text-orange-600 mt-2">
                    {eligible[0].reminderCount} reminder(s) previously sent
                  </p>
                )}
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-gray-700 mb-2">Selected invitations:</p>
                <ul className="text-xs text-gray-600 space-y-1">
                  {eligible.map((inv) => (
                    <li key={inv.id} className="flex items-start gap-2">
                      <Bell className="w-3.5 h-3.5 text-orange-500 mt-0.5 flex-shrink-0" />
                      <span>
                        <span className="font-medium">{inv.manuscriptId || inv.id}</span>
                        <span className="text-gray-500">
                          {' '}— {inv.invitedTo} ({inv.expiryDate || 'No expiry'})
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}

        {/* Extend expiry toggle */}
        <div className="mt-4">
          <div className="flex items-center justify-between">
            <label
              htmlFor="reminderExtendToggle"
              className="text-sm font-medium text-gray-700"
            >
              Extend Invitation Expiry {eligible.length > 1 ? 'Dates' : 'Date'}
            </label>
            <button
              type="button"
              role="switch"
              aria-checked={extendExpiry}
              id="reminderExtendToggle"
              onClick={() => {
                setExtendExpiry(!extendExpiry);
                if (extendExpiry) setExtendDays('');
              }}
              className={`relative inline-flex items-center h-6 w-11 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                extendExpiry ? 'bg-orange-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block w-4 h-4 transform rounded-full bg-white shadow transition-transform ${
                  extendExpiry ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {extendExpiry && (
          <div className="mt-3">
            <label
              htmlFor="reminderExtendDays"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Number of Days to Extend
            </label>
            <input
              id="reminderExtendDays"
              type="number"
              min="1"
              step="1"
              value={extendDays}
              onChange={(e) => setExtendDays(e.target.value)}
              placeholder="e.g. 14"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            {previewNewExpiry && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800">
                  Current expiry:{' '}
                  <span className="font-medium">
                    {formatDate(eligible[0].expiryDate)}
                  </span>
                </p>
                <p className="text-sm text-blue-900 mt-1 font-medium">
                  New expiry date: {formatDate(previewNewExpiry.toISOString())}
                </p>
              </div>
            )}
            {eligible.length > 1 && (
              <p className="mt-2 text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-lg p-2">
                Each invitation's new expiry will be calculated from its own current
                expiry plus the number of days set above.
              </p>
            )}
          </div>
        )}

        {/* Notes */}
        <div className="mt-4">
          <label
            htmlFor="reminderNotes"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Notes / Custom Message
          </label>
          <textarea
            id="reminderNotes"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional: include a custom message in the reminder email (visible to the recipient)"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 resize-y"
          />
        </div>

        {/* Info banner if some invitations were ineligible */}
        {invitations.length > eligible.length && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-xs text-yellow-800 flex items-start">
              <AlertTriangle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
              <span>
                {invitations.length - eligible.length} invitation
                {invitations.length - eligible.length !== 1 ? 's' : ''} in the
                selection are not eligible for reminders (response already received
                or invitation cancelled) and will be skipped.
              </span>
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={sending}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={sending || eligible.length === 0}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Sending…
              </>
            ) : (
              <>
                <Bell className="w-4 h-4" />
                {eligible.length === 1
                  ? 'Send Reminder'
                  : `Send to ${eligible.length}`}
              </>
            )}
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

export default SendReminderModal;